// openEHR RM model — BMM package org.openehr.rm.ehr_extract.common
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import { LOCATABLE } from "../common/archetyped.ts";
import type { ITEM_STRUCTURE } from "../data_structures/item_structure.ts";
import type { DV_DATE_TIME, DV_DURATION } from "../data_types/quantity/date_time.ts";
import type { CODE_PHRASE, DV_CODED_TEXT, DV_TEXT } from "../data_types/text.ts";
import * as openehr_base from "../../base/mod.ts";
import { applyRmInit, type RmInit } from "../../base/apply_rm_init.ts";

/**
 * Generic model of an Extract of some information from a repository.
 */
export class EXTRACT extends LOCATABLE {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * The content extracted and serialised from the source repository for this Extract.
   */
  chapters?: undefined;
  /**
   * The specification that this Extract actually conforms to; might not be identical with the specification of the corresponding request.
   */
  specification?: EXTRACT_SPEC;
  /**
   * Reference to causing Request, if any.
   */
  request_id?: openehr_base.HIER_OBJECT_ID;
  /**
   * Creation time of this Extract
   */
  time_created?: DV_DATE_TIME;
  /**
   * Identifier of creating system.
   */
  system_id?: openehr_base.HIER_OBJECT_ID;
  /**
   * Internal storage for sequence_nr
   * @protected
   */
  protected _sequence_nr?: openehr_base.Integer;

  /**
   * Number of this Extract response in sequence of responses to Extract request identified by \`_request_id_\`. If this is the sole response, or there was no request, value is 1.
   */
  get sequence_nr(): number | undefined {
    return this._sequence_nr?.value;
  }

  /**
   * Gets the openehr_base.Integer wrapper object for sequence_nr.
   * Use this to access openehr_base.Integer methods.
   */
  get $sequence_nr(): openehr_base.Integer | undefined {
    return this._sequence_nr;
  }

  /**
   * Sets sequence_nr from either a primitive value or openehr_base.Integer wrapper.
   */
  set sequence_nr(val: number | openehr_base.Integer | undefined) {
    if (val === undefined || val === null) {
      this._sequence_nr = undefined;
    } else if (typeof val === "number") {
      this._sequence_nr = openehr_base.Integer.from(val);
    } else {
      this._sequence_nr = val;
    }
  }

  /**
   * Participations relevant to the creation of this Extract.
   */
  participations?: undefined;
}

/**
 * Generic model of a Request for an Extract, containing an Extract specification.
 */
export class EXTRACT_ACTION_REQUEST extends LOCATABLE {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Identifier of previous \`EXTRACT_REQUEST\`.
   */
  request_id?: openehr_base.OBJECT_REF;
  override uid?: openehr_base.HIER_OBJECT_ID = undefined;
  /**
   * Requested action: \`cancel | resend | send new\`. Coded by openEHR Terminology group \`'extract action type'\`.
   */
  action?: DV_CODED_TEXT;
}

/**
 * One content chapter of an Extract; contains information relating to only one entity.
 */
export class EXTRACT_CHAPTER extends LOCATABLE {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * The information content of this chapter.
   */
  items?: undefined;
}

/**
 * Abstract parent of Extract Folder and Content types.
 */
export abstract class EXTRACT_ITEM extends LOCATABLE {
}

/**
 * Abstract model of a wrapper for one content item in an Extract, containing various meta-data. Indicates whether it was part of the primary set and what its original path was. Intended to be subtyped for wrappers of specific types of content.
 */
export abstract class EXTRACT_CONTENT_ITEM extends EXTRACT_ITEM {
  /**
   * Internal storage for is_primary
   * @protected
   */
  protected _is_primary?: openehr_base.Boolean;

  /**
   * True if the content item carried in this container was part of the primary set for the Extract, i.e. not added due to link-following.
   */
  get is_primary(): boolean | undefined {
    return this._is_primary?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_primary.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_primary(): openehr_base.Boolean | undefined {
    return this._is_primary;
  }

  /**
   * Sets is_primary from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_primary(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_primary = undefined;
    } else if (typeof val === "boolean") {
      this._is_primary = openehr_base.Boolean.from(val);
    } else {
      this._is_primary = val;
    }
  }

  /**
   * Internal storage for is_changed
   * @protected
   */
  protected _is_changed?: openehr_base.Boolean;

  /**
   * True if the content item carried in this container is any kind of change since last send, in repeat sending situations.
   */
  get is_changed(): boolean | undefined {
    return this._is_changed?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_changed.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_changed(): openehr_base.Boolean | undefined {
    return this._is_changed;
  }

  /**
   * Sets is_changed from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_changed(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_changed = undefined;
    } else if (typeof val === "boolean") {
      this._is_changed = openehr_base.Boolean.from(val);
    } else {
      this._is_changed = val;
    }
  }

  /**
   * Internal storage for is_masked
   * @protected
   */
  protected _is_masked?: openehr_base.Boolean;

  /**
   * True if the content of this item has not been included due to insufficient access rights of requestor.
   */
  get is_masked(): boolean | undefined {
    return this._is_masked?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_masked.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_masked(): openehr_base.Boolean | undefined {
    return this._is_masked;
  }

  /**
   * Sets is_masked from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_masked(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_masked = undefined;
    } else if (typeof val === "boolean") {
      this._is_masked = openehr_base.Boolean.from(val);
    } else {
      this._is_masked = val;
    }
  }

  /**
   * Content object.
   */
  item?: openehr_base.Any;
}

/**
 * Type of chapter that contains information relating to a single demographic entity.
 */
export class EXTRACT_ENTITY_CHAPTER extends EXTRACT_CHAPTER {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Internal storage for extract_id_key
   * @protected
   */
  protected _extract_id_key?: openehr_base.String;

  /**
   * Reference to entity, usually a demographic entity such as a patient that the content of this chapter relates to.
   */
  get extract_id_key(): string | undefined {
    return this._extract_id_key?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for extract_id_key.
   * Use this to access openehr_base.String methods.
   */
  get $extract_id_key(): openehr_base.String | undefined {
    return this._extract_id_key;
  }

  /**
   * Sets extract_id_key from either a primitive value or openehr_base.String wrapper.
   */
  set extract_id_key(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._extract_id_key = undefined;
    } else if (typeof val === "string") {
      this._extract_id_key = openehr_base.String.from(val);
    } else {
      this._extract_id_key = val;
    }
  }
}

/**
 * The manifest for one entity (e.g. EHR subject), identifying the entity and optionally specifying top-level items to be included in the Extract. The list actually included may be modified by the \`_version_spec_\` part of the specification, and also by the link_depth attribute. In repeat (standing order) requests, the final inclusion may be modified by the send_changes_only value for \`EXTRACT_UPDATE_SPEC._update_method_\`.
 *
 * Various identifiers may be provided for the entity; these are to be used by the receiver system to locate the entity. The \`_extract_id_key_\` attribute is used to record the identifier that will be used throughout the Extract for this entity, including in instances of \`EXTRACT_ENTITY_IDENTIFIER\`.
 */
export class EXTRACT_ENTITY_MANIFEST {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * Internal storage for extract_id_key
   * @protected
   */
  protected _extract_id_key?: openehr_base.String;

  /**
   * Identifier by which this entity is known in the Extract. May be one of the other identifiers, e.g. ehr_id or subject_id, or it may be something else, including a simple integer.
   */
  get extract_id_key(): string | undefined {
    return this._extract_id_key?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for extract_id_key.
   * Use this to access openehr_base.String methods.
   */
  get $extract_id_key(): openehr_base.String | undefined {
    return this._extract_id_key;
  }

  /**
   * Sets extract_id_key from either a primitive value or openehr_base.String wrapper.
   */
  set extract_id_key(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._extract_id_key = undefined;
    } else if (typeof val === "string") {
      this._extract_id_key = openehr_base.String.from(val);
    } else {
      this._extract_id_key = val;
    }
  }

  /**
   * Internal storage for ehr_id
   * @protected
   */
  protected _ehr_id?: openehr_base.String;

  /**
   * EHR / EMR identifier for the entity at the target system.
   */
  get ehr_id(): string | undefined {
    return this._ehr_id?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for ehr_id.
   * Use this to access openehr_base.String methods.
   */
  get $ehr_id(): openehr_base.String | undefined {
    return this._ehr_id;
  }

  /**
   * Sets ehr_id from either a primitive value or openehr_base.String wrapper.
   */
  set ehr_id(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._ehr_id = undefined;
    } else if (typeof val === "string") {
      this._ehr_id = openehr_base.String.from(val);
    } else {
      this._ehr_id = val;
    }
  }

  /**
   * Internal storage for subject_id
   * @protected
   */
  protected _subject_id?: openehr_base.String;

  /**
   * Subject (i.e. patient or similar) identifier for the entity at the target system.
   */
  get subject_id(): string | undefined {
    return this._subject_id?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for subject_id.
   * Use this to access openehr_base.String methods.
   */
  get $subject_id(): openehr_base.String | undefined {
    return this._subject_id;
  }

  /**
   * Sets subject_id from either a primitive value or openehr_base.String wrapper.
   */
  set subject_id(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._subject_id = undefined;
    } else if (typeof val === "string") {
      this._subject_id = openehr_base.String.from(val);
    } else {
      this._subject_id = val;
    }
  }

  /**
   * Other identifiers that may be used to find the entity at the target system, keyed by type. May include medicare numbers, drivers license number, tax number etc.
   */
  other_ids?: undefined;
  /**
   * List of Uids of items to be included in the Extract, in cases where individual items are being requested by id. More typically, this attribute is not used, and the \`EXTRACT_SPEC._criteria query_\` defines the Extract contents. If set, for openEHR data, these are Uids identifying the version containers.
   */
  item_list?: undefined;
}

export class EXTRACT_ERROR {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  request_id?: openehr_base.OBJECT_REF;
  reason?: DV_TEXT;
}

/**
 * Folder in local Folder structure in an Extract. Empty Folders are allowed.
 */
export class EXTRACT_FOLDER extends EXTRACT_ITEM {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * List of Folders and content items in this Folder.
   */
  items?: undefined;
}

/**
 * Specification of the candidate entities and optionally top-level items (e.g. Compositions) to be included in the Extract.
 */
export class EXTRACT_MANIFEST {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * List of entity manifests uids of items included in the Extract; for openEHR data, these are uids identifying the version containers.
   */
  entities?: undefined;
}

/**
 * Model of a participation of a Party (any Actor or Role) in an activity.  Used to represent any participation of a Party in some activity, which is not  explicitly in the model, e.g. assisting nurse. Can be used to record past or  future participations.
 *
 * Should not be used in place of more permanent relationships between demographic entities.
 */
export class EXTRACT_PARTICIPATION {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * The time interval during which the participation took place, if it is used in an observational context (i.e. recording facts about the past); or the intended time interval of the participation when used in future contexts, such as EHR Instructions.
   */
  time?: undefined;
  /**
   * The function of the Party in this participation (note that a given party might participate in more than one way in a particular activity). This attribute should be coded, but cannot be limited to the HL7v3:ParticipationFunction vocabulary, since it is too limited and hospital-oriented.
   */
  function?: DV_TEXT;
  /**
   * The mode of the performer / activity interaction, e.g. present, by telephone, by email etc.
   */
  mode?: DV_CODED_TEXT;
  /**
   * Internal storage for performer
   * @protected
   */
  protected _performer?: openehr_base.String;

  /**
   * Uid of demographic entity within Extract who performed this participation.
   */
  get performer(): string | undefined {
    return this._performer?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for performer.
   * Use this to access openehr_base.String methods.
   */
  get $performer(): openehr_base.String | undefined {
    return this._performer;
  }

  /**
   * Sets performer from either a primitive value or openehr_base.String wrapper.
   */
  set performer(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._performer = undefined;
    } else if (typeof val === "string") {
      this._performer = openehr_base.String.from(val);
    } else {
      this._performer = val;
    }
  }
}

/**
 * Generic model of a Request for an Extract, containing an Extract specification.
 */
export class EXTRACT_REQUEST extends LOCATABLE {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Specification details of the request.
   */
  extract_spec?: EXTRACT_SPEC;
  /**
   * Update details of the request.
   */
  update_spec?: EXTRACT_UPDATE_SPEC;
  /**
   * Identifier of this Request, generated by requestor.
   */
  override uid?: openehr_base.HIER_OBJECT_ID = undefined;
}

/**
 * Specification of an Extract's contents. Subtypes can be used to add details specific to the type of Extract. The specification consists of attributes specifying the directory, and two further groups of attributes in their own classes, namely a version specfication (which versions of information items are to be included) and a manifest (which entities are to be included in the extract).
 *
 * Use: Used in a request to specify an Extract, as well as to describe what is contained in an Extract.
 */
export class EXTRACT_SPEC {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * Specification of which versions of information items to include in the Extract. If Void, the default is latest versions only (which is reasonable for non-versioning systems as well).
   */
  version_spec?: EXTRACT_VERSION_SPEC;
  /**
   * Specification of entities (e.g. records) to include in the Extract.
   */
  manifest?: EXTRACT_MANIFEST;
  /**
   * Coded term indicating the type of content required, e.g.
   *
   * * \`|openehr-ehr|\`
   * * \`|openehr-demographic|\`
   * * \`|generic-emr|\`
   * * \`|other|\`
   *
   * Coded by openEHR Terminology group \`'extract content type'\`.
   */
  extract_type?: DV_CODED_TEXT;
  /**
   * Internal storage for include_multimedia
   * @protected
   */
  protected _include_multimedia?: openehr_base.Boolean;

  /**
   * Indicates whether in-line instances of \`DV_MULTIMEDIA\` in the source data are included or not.
   */
  get include_multimedia(): boolean | undefined {
    return this._include_multimedia?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for include_multimedia.
   * Use this to access openehr_base.Boolean methods.
   */
  get $include_multimedia(): openehr_base.Boolean | undefined {
    return this._include_multimedia;
  }

  /**
   * Sets include_multimedia from either a primitive value or openehr_base.Boolean wrapper.
   */
  set include_multimedia(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._include_multimedia = undefined;
    } else if (typeof val === "boolean") {
      this._include_multimedia = openehr_base.Boolean.from(val);
    } else {
      this._include_multimedia = val;
    }
  }

  /**
   * Internal storage for priority
   * @protected
   */
  protected _priority?: openehr_base.Integer;

  /**
   * Requested priority of this request to be handled by server. Priority schemes are likely to be local, and use values agreed by both ends.
   *
   * TBD: alternative is standard coded terms
   */
  get priority(): number | undefined {
    return this._priority?.value;
  }

  /**
   * Gets the openehr_base.Integer wrapper object for priority.
   * Use this to access openehr_base.Integer methods.
   */
  get $priority(): openehr_base.Integer | undefined {
    return this._priority;
  }

  /**
   * Sets priority from either a primitive value or openehr_base.Integer wrapper.
   */
  set priority(val: number | openehr_base.Integer | undefined) {
    if (val === undefined || val === null) {
      this._priority = undefined;
    } else if (typeof val === "number") {
      this._priority = openehr_base.Integer.from(val);
    } else {
      this._priority = val;
    }
  }

  /**
   * Internal storage for link_depth
   * @protected
   */
  protected _link_depth?: openehr_base.Integer;

  /**
   * Degree of links to follow emanating from content items specified for inclusion. The kind of links to follow is dependent on the type of Extract.
   *
   * All items at the target end of followed links at the given depth are also included in the extract; \`EXTRACT_CONTENT_ITEM._is_primary_\` is used to differentiate.
   *
   * * 0 = don't follow;
   * * 1 = follow first degree links;
   * * 2 = follow 2nd degree links;
   * * ....
   * * n = follow nth degree links
   */
  get link_depth(): number | undefined {
    return this._link_depth?.value;
  }

  /**
   * Gets the openehr_base.Integer wrapper object for link_depth.
   * Use this to access openehr_base.Integer methods.
   */
  get $link_depth(): openehr_base.Integer | undefined {
    return this._link_depth;
  }

  /**
   * Sets link_depth from either a primitive value or openehr_base.Integer wrapper.
   */
  set link_depth(val: number | openehr_base.Integer | undefined) {
    if (val === undefined || val === null) {
      this._link_depth = undefined;
    } else if (typeof val === "number") {
      this._link_depth = openehr_base.Integer.from(val);
    } else {
      this._link_depth = val;
    }
  }

  /**
   * Queries specifying the contents of this Extract.
   */
  criteria?: undefined;
  /**
   * Other specification items. Archetypable.
   */
  other_details?: ITEM_STRUCTURE;
}

/**
 * Specification of the how the request should be processed by server. The request can be persisted in the server, meaning that a) it can be re-activated by the requesting system simply by indicating Request id, and b) that a changes-only pattern of Extract updates can be set up. To achieve this, the server has to remember what was sent in the previous response.
 *
 * The update mode may be event-driven and periodic update or a mixture of both. The candidate items to be sent each time are the result of re-evaluating the content and versioning parts of the specification; what is actually sent is determined by the \`_send_changes_only_\` flag.
 */
export class EXTRACT_UPDATE_SPEC {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * Internal storage for persist_in_server
   * @protected
   */
  protected _persist_in_server?: openehr_base.Boolean;

  /**
   * If True, this Request is persisted in the server until further notice.
   */
  get persist_in_server(): boolean | undefined {
    return this._persist_in_server?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for persist_in_server.
   * Use this to access openehr_base.Boolean methods.
   */
  get $persist_in_server(): openehr_base.Boolean | undefined {
    return this._persist_in_server;
  }

  /**
   * Sets persist_in_server from either a primitive value or openehr_base.Boolean wrapper.
   */
  set persist_in_server(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._persist_in_server = undefined;
    } else if (typeof val === "boolean") {
      this._persist_in_server = openehr_base.Boolean.from(val);
    } else {
      this._persist_in_server = val;
    }
  }

  /**
   * Period for resending update Extracts in response to original Request.
   */
  repeat_period?: DV_DURATION;
  /**
   * Set of Event names that will cause sending of update Extracts. Event types include:
   *
   * * \`|any_change|\` - any change in content items matched by content specification, e.g. new versions of persistent compositions. If the content list allows matching of any, or a wide range of archetypes, this event type will match any additions to the record.
   * * \`|correction|\` - match error corrections only, including deletions.
   * * \`|update|\` - match updates (i.e. new versions) of included content items.
   *
   * Coded by openEHR Terminology group 'extract update trigger event type'.
   */
  trigger_events?: undefined;
  /**
   * Indicate mode of update. Can be: send only items that are changed (including logical deletions) or new since last send. For \`_persist_in_server_\` Requests only.
   */
  update_method?: CODE_PHRASE;
}

/**
 * Specification of what versions should be included in an Extract. By default, only latest versions are included in the Extract, in which case this part of the Extract specification is not needed at all. The attributes \`_include_all_versions_\` and \`_commit_time_interval_\` are used to modify this; the former forces all versions to be included; the latter limits the versions to be those latest versions committed in the time interval, or if \`_include_all_versions_\` is \`True\`, all versions committed in the time interval.
 */
export class EXTRACT_VERSION_SPEC {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * Internal storage for include_all_versions
   * @protected
   */
  protected _include_all_versions?: openehr_base.Boolean;

  /**
   * True if all versions of each item in the Extract are included.
   */
  get include_all_versions(): boolean | undefined {
    return this._include_all_versions?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for include_all_versions.
   * Use this to access openehr_base.Boolean methods.
   */
  get $include_all_versions(): openehr_base.Boolean | undefined {
    return this._include_all_versions;
  }

  /**
   * Sets include_all_versions from either a primitive value or openehr_base.Boolean wrapper.
   */
  set include_all_versions(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._include_all_versions = undefined;
    } else if (typeof val === "boolean") {
      this._include_all_versions = openehr_base.Boolean.from(val);
    } else {
      this._include_all_versions = val;
    }
  }

  /**
   * Specifies commit time interval of items to source repository to include in Extract. By default, only latest versions whose commit times fall in the range are included. If include_all_versions is True, then the range includes all versions committed within the interval.
   */
  commit_time_interval?: undefined;
  /**
   * Internal storage for include_revision_history
   * @protected
   */
  protected _include_revision_history?: openehr_base.Boolean;

  /**
   * True if revision histories of the items in the Extract are included. If included, it is always the full revision history.
   */
  get include_revision_history(): boolean | undefined {
    return this._include_revision_history?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for include_revision_history.
   * Use this to access openehr_base.Boolean methods.
   */
  get $include_revision_history(): openehr_base.Boolean | undefined {
    return this._include_revision_history;
  }

  /**
   * Sets include_revision_history from either a primitive value or openehr_base.Boolean wrapper.
   */
  set include_revision_history(
    val: boolean | openehr_base.Boolean | undefined,
  ) {
    if (val === undefined || val === null) {
      this._include_revision_history = undefined;
    } else if (typeof val === "boolean") {
      this._include_revision_history = openehr_base.Boolean.from(val);
    } else {
      this._include_revision_history = val;
    }
  }

  /**
   * Internal storage for include_data
   * @protected
   */
  protected _include_data?: openehr_base.Boolean;

  /**
   * \`True\` if the data of items matched by the content spec should be included. This is the default. If \`False\`, only revision history is included in serialised versions. Turning this option on in openEHR systems causes \`X_VERSIONED_OBJECTs\` to have \`_revision_history_\` set, but versions Void. Useful for interrogating a server without having to look at any content data. In other systems it may or may not have a sensible meaning.
   */
  get include_data(): boolean | undefined {
    return this._include_data?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for include_data.
   * Use this to access openehr_base.Boolean methods.
   */
  get $include_data(): openehr_base.Boolean | undefined {
    return this._include_data;
  }

  /**
   * Sets include_data from either a primitive value or openehr_base.Boolean wrapper.
   */
  set include_data(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._include_data = undefined;
    } else if (typeof val === "boolean") {
      this._include_data = openehr_base.Boolean.from(val);
    } else {
      this._include_data = val;
    }
  }
}
